<template>
  <div id="modal-selecionar-empresa" class="modal fade" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 id="ModalLabel" class="modal-title">
            {{ title }}
          </h5>
        </div>
        <div class="modal-body">
          <DataTable
            :items="empresasComStatus"
            :columns="columns"
            :pagination="pagination"
            :loading="loading"
            :search-value="search"
            :search-label="'Pesquisar'"
            :search-placeholder="'ID, Nome Fantasia, CNPJ, Cidade'"
            :empty-message="'Nenhuma empresa disponível ou filtro não retornou resultados'"
            @search="onSearchChanged"
            @page-change="mudarPagina"
          >
            <!-- Template para coluna de ações -->
            <template #actions="{ item }">
              <button 
                type="button" 
                class="btn btn-link text-success text-gradient px-3 mb-0"
                :disabled="idsJaSelecionados.includes(item.id)"
                @click="adicionarEmpresa(item)"
                data-bs-dismiss="modal">
                <i class="fas fa-plus"></i> Adicionar
              </button>
            </template>
          </DataTable>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn bg-gradient-dark btn-sm" data-bs-dismiss="modal" ref="closeModalButton">
            Fechar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import debounce from 'lodash/debounce';
import DataTable from '@/components/DataTable.vue';

export default {
  name: 'EmpresaSeletorModal',
  components: {
    DataTable
  },
  props: {
    empresasJaSelecionadas: {
      type: Array,
      default: () => []
    },
    title: {
      type: String,
      default: 'Selecionar Empresa'
    }
  },
  emits: ['adicionar-empresa'],
  data() {
    return {
      empresas: [],
      loading: false,
      search: '',
      currentPage: 1,
      pageSize: 10,
      total: 0,
      totalPages: 0,
      orderBy: 'id',
      orderDirection: 'DESC',
      columns: [
        { key: 'id', label: 'ID' },
        { key: 'nomeFantasia', label: 'Nome Fantasia' },
        { 
          key: 'cnpj', 
          label: 'CNPJ', 
          formatter: this.formatCpfCnpj
        },
        { 
          key: 'cidade', 
          label: 'Cidade',
          formatter: this.formatCidade
        },
        { key: 'actions', label: 'Ações', headerClass: 'text-center', class: 'text-center' }
      ]
    };
  },
  computed: {
    // Obter IDs das empresas já selecionadas
    idsJaSelecionados() {
      if (!this.empresasJaSelecionadas || this.empresasJaSelecionadas.length === 0) {
        return [];
      }
      
      return this.empresasJaSelecionadas.map(item => {
        // Compatibilidade com o novo formato
        return typeof item === 'object' && item.empresa ? item.empresa.id : 
               typeof item === 'object' && item.empresa_id ? item.empresa_id : 
               item;
      });
    },
    
    // Adicionar uma flag para cada empresa indicando se já está selecionada
    empresasComStatus() {
      return this.empresas.map(empresa => {
        const isSelected = this.idsJaSelecionados.includes(empresa.id);
        return {
          ...empresa,
          _rowClass: isSelected ? 'empresa-selecionada' : ''
        };
      });
    },
    
    // Objeto de paginação para o DataTable
    pagination() {
      return {
        currentPage: this.currentPage,
        pageSize: this.pageSize,
        total: this.total,
        totalPages: this.totalPages,
        hasPreviousPage: this.currentPage > 1,
        hasNextPage: this.currentPage < this.totalPages
      };
    }
  },
  created() {
    // Criar versão debounced do método buscarEmpresas para evitar chamadas excessivas
    this.debouncedBuscar = debounce(this.buscarEmpresas, 300);
  },
  mounted() {
    // Quando o modal é aberto, buscar os dados
    document.getElementById('modal-selecionar-empresa').addEventListener('show.bs.modal', () => {
      this.resetarFiltros();
      this.buscarEmpresas();
    });
  },
  methods: {
    resetarFiltros() {
      this.search = '';
      this.currentPage = 1;
      this.orderBy = 'id';
      this.orderDirection = 'DESC';
    },
    
    buscarEmpresas() {
      this.loading = true;
      
      const params = {
        page: this.currentPage,
        limit: this.pageSize,
        search: this.search,
        orderBy: this.orderBy,
        orderDirection: this.orderDirection
      };
      
      api.get('/clientes/empresas/paginate', { params })
        .then(response => {
          this.empresas = response.data.items;
          this.total = response.data.total;
          this.totalPages = response.data.totalPages;
          this.loading = false;
        })
        .catch(error => {
          console.error('Erro ao buscar empresas:', error);
          this.loading = false;
        });
    },
    
    // Método chamado quando o input de busca muda
    onSearchChanged(value) {
      this.search = value;
      this.currentPage = 1; // Voltar para a primeira página ao buscar
      this.debouncedBuscar();
    },
    
    // Mudar de página
    mudarPagina(pagina) {
      if (pagina < 1 || pagina > this.totalPages || pagina === this.currentPage) {
        return;
      }
      
      this.currentPage = pagina;
      this.buscarEmpresas();
    },
    
    adicionarEmpresa(empresa) {
      this.$emit('adicionar-empresa', empresa);
    },
    
    formatCpfCnpj(value) {
      if (!value) return '';
      
      // Para CNPJ (14 dígitos)
      if (value.length === 14) {
        return value.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
      }
      
      // Para CPF (11 dígitos)
      if (value.length === 11) {
        return value.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
      }
      
      return value;
    },
    
    formatCidade(value) {
      if (!value) return '';
      
      if (typeof value === 'object' && value !== null) {
        return value.descricao || '';
      }
      
      return value;
    }
  }
};
</script>

<style lang="scss" scoped>
.empresa-selecionada {
  font-weight: bold !important;
  color: #344767 !important;
  border-left: 4px solid #2eb85c !important;
}
</style> 